import InvoiceHomeDetailCard from "./InvoiceHomeDetailCard";
import { getInvoiceDetailsCardInfo } from "../invoice-master/invoice-helper";
import { RootState } from "src/context/store";
import { useSelector, useDispatch } from "react-redux";
import { useCallback, useEffect, useMemo, useState } from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import { Spinner } from "src/components";
import { invoiceActions } from "src/context/invoice-slice";
import { fetchEscalatedInvoicesData } from "src/services/invoice-api";
import { useToast } from "src/hooks/useToast";

const InvoiceEscalatedTab = ({ searchValue, searchCategory }: { searchValue?: string; searchCategory?: string }) => {
  const { invoiceEscalatedData, invoiceAnalysisData } = useSelector((state: RootState) => state.invoice);
  const [hasMore, setHasMore] = useState(true);
  const { InvoiceFilters } = useSelector((state: RootState) => state.invoice);
  const [page, setPage] = useState(2);
  const [isLoading, setIsLoading] = useState(false);
  const dispatch = useDispatch();
  const { toast } = useToast();

  const searchParams = useMemo(
    () => ({
      company_code: InvoiceFilters?.company?.map((item) => item.code)?.join(",") || "",
      division_code: InvoiceFilters?.division?.map((item) => item.company_divisioncode)?.join(",") || "",
      startDate: InvoiceFilters?.dateRange?.from,
      endDate: InvoiceFilters?.dateRange?.to,
      assignee_id: InvoiceFilters?.assignee?.map((item) => item.invoice_assignment_id)?.join(",") || "",
      city: InvoiceFilters?.city?.map((item) => item.city_name?.split("|")?.[0]?.trim())?.join(",") || "",
      district: InvoiceFilters?.district?.map((item) => item.district_name)?.join(",") || "",
      state: InvoiceFilters?.state?.map((item) => item.state_name)?.join(",") || "",
      ordering: InvoiceFilters?.sorting?.[0]?.key || "DESC",
      fuid_status_id: searchValue && searchValue?.length > 0 ? "" : InvoiceFilters?.status?.map((item) => item.fuid_status_id)?.join(",") || 1,
      page_size: 10,
      page_number: 1,
      screen: "Entry",
      ...(searchValue && searchCategory ? { [searchCategory]: searchValue } : {}),
    }),
    [searchValue, searchCategory, InvoiceFilters]
  );

  const fetchTabData = useCallback(
    async () => {
      setIsLoading(true);
      dispatch(invoiceActions.setInvoiceDetails([]));
      dispatch(invoiceActions.setInvoicesTextractData([]));
      window.localStorage.removeItem("counterCompanyLocationId");
      window.localStorage.removeItem("counterOrderId");
      try {
        const allInvoicesRes = await fetchEscalatedInvoicesData(searchParams);
        dispatch(invoiceActions.setInvoiceEscalatedData(allInvoicesRes?.data?.data?.invoiceData || []));
        if (allInvoicesRes?.data?.data?.total > 0 && invoiceAnalysisData?.entry?.["All Invoices"] > 0) {
          dispatch(invoiceActions.setInvoiceAnalysisData(
            {
              entry: {
                "All Invoices": invoiceAnalysisData?.entry?.["All Invoices"] || 0,
                "Priority Tasks": invoiceAnalysisData?.entry?.["Priority Tasks"] || 0,
                "Escalated":  allInvoicesRes?.data?.data?.total || 0,
              },
              approval: {
                "All Invoices": invoiceAnalysisData?.approval?.["All Invoices"] || 0,
                "Priority Tasks": invoiceAnalysisData?.approval?.["Priority Tasks"] || 0,
                "Escalated": invoiceAnalysisData?.approval?.["Escalated"] || 0,
              }
            }
          ));
        }
        setPage(2);
        setHasMore(true);
        toast({ description: "Invoice data fetched successfully", variant: "default" });
      } catch (error) {
        console.error("Error fetching invoices data:", error);
        toast({ description: "Failed to fetch invoice data", variant: "destructive" });
      } finally {
        setIsLoading(false);
      }
    },
    [dispatch, searchParams]
  );

  const fetchMoreData = async () => {
    if (isLoading) return;
    try {
      const params = { ...searchParams, page_number: page };
      const response = await fetchEscalatedInvoicesData(params);
      const newData = response?.data?.data?.invoiceData;
      if (!newData || newData.length === 0) {
        setHasMore(false);
        return;
      }
      dispatch(invoiceActions.setInvoiceEscalatedData([...(invoiceEscalatedData || []), ...newData]));
      setPage((prev) => prev + 1);
    } catch (error) {
      console.error("Error fetching more invoices:", error);
      setHasMore(false);
    }
  };

  useEffect(() => {
    fetchTabData();
  }, [fetchTabData]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-[50vh]">
        {" "}
        <Spinner />
      </div>
    );
  }

  return (
    <div className="mt-10 w-full">
      {invoiceEscalatedData?.length === 0 && (
        <div className="flex justify-center items-center h-[20vh]">
          <p className="text-lg text-gray-500">No invoices found</p>
        </div>
      )}

      <div id="scrollableDiv" className="h-screen overflow-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
        <InfiniteScroll
          dataLength={invoiceEscalatedData?.length}
          next={fetchMoreData}
          hasMore={hasMore}
          loader={""}
          scrollableTarget="scrollableDiv"
          endMessage={<div className="py-4 text-center text-gray-500">No more invoices to load</div>}
        >
          <ul>
            {invoiceEscalatedData?.map((data: any, index: number) => (
              <li className="my-5 rounded-xl border shadow-sm border-gray-200" key={index}>
                <InvoiceHomeDetailCard
                  status={data?.fileStatusHdr?.fuid_status_desc}
                  data={getInvoiceDetailsCardInfo(data)}
                  checkDataType={false}
                  invoiceData={data}
                  viewDetails={true}
                  index={index}
                />
              </li>
            ))}
          </ul>
        </InfiniteScroll>
      </div>
    </div>
  );
};

export default InvoiceEscalatedTab;
